/**
 * WidgetConfigDrawer Component
 * Drawer for creating and editing dashboard widgets
 */

import React, { useEffect } from 'react';
import {
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  Divider,
  InputNumber,
  DatePicker,
  Card,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  UnorderedListOutlined,
  NumberOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { DashboardWidget, CreateWidgetInput, UpdateWidgetInput } from '@/api/dashboard-widgets';
import { categoriesApi } from '@/api/categories';
import { tagsApi } from '@/api/tags';
import { usersApi } from '@/api/users';
import { templatesApi } from '@/api/templates';

const { Text } = Typography;

interface WidgetConfigDrawerProps {
  open: boolean;
  widget?: DashboardWidget | null;
  onClose: () => void;
  onSave: (data: CreateWidgetInput | UpdateWidgetInput) => Promise<void>;
}

const WIDGET_TYPES = [
  {
    value: 'list',
    label: 'Work Package List',
    icon: <UnorderedListOutlined />,
    description: 'Shows a list of work packages matching your filters',
  },
  {
    value: 'count',
    label: 'Count / Statistic',
    icon: <NumberOutlined />,
    description: 'Shows a count of work packages matching your filters',
  },
  {
    value: 'calendar',
    label: 'Calendar View',
    icon: <CalendarOutlined />,
    description: 'Shows due dates on a mini calendar',
  },
];

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'next_3_days', label: 'Next 3 Days' },
  { value: 'next_7_days', label: 'Next 7 Days' },
  { value: 'next_14_days', label: 'Next 2 Weeks' },
  { value: 'next_30_days', label: 'Next 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'custom', label: 'Custom Range...' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
];

const SORT_OPTIONS = [
  { value: 'due_date', label: 'Due Date' },
  { value: 'created_at', label: 'Created Date' },
  { value: 'title', label: 'Title' },
  { value: 'category', label: 'Category' },
  { value: 'assignee', label: 'Assignee' },
];

const WidgetConfigDrawer: React.FC<WidgetConfigDrawerProps> = ({
  open,
  widget,
  onClose,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);

  const isEditing = !!widget;
  const widgetType = Form.useWatch('widget_type', form);
  const dueDateRange = Form.useWatch(['filter_config', 'dueDateRange'], form);

  // Fetch data for selectors
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  const { data: tags } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.listTenantMembers(),
  });

  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templatesApi.list(),
  });

  // Reset form when widget changes
  useEffect(() => {
    if (open) {
      if (widget) {
        form.setFieldsValue({
          title: widget.title,
          widget_type: widget.widget_type,
          filter_config: widget.filter_config,
          display_config: widget.display_config,
          grid_width: widget.grid_width,
          grid_height: widget.grid_height,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          widget_type: 'list',
          filter_config: {
            assignee: 'me',
            status: ['pending', 'in_progress'],
          },
          display_config: {
            maxItems: 10,
            sortBy: 'due_date',
            sortOrder: 'asc',
            showAssignee: true,
            showCategory: true,
            showDueDate: true,
          },
          grid_width: 2,
          grid_height: 2,
        });
      }
    }
  }, [open, widget, form]);

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();

      // Clean up the data
      const data: CreateWidgetInput | UpdateWidgetInput = {
        title: values.title,
        widget_type: values.widget_type,
        filter_config: values.filter_config || {},
        display_config: values.display_config || {},
        grid_width: values.grid_width,
        grid_height: values.grid_height,
      };

      await onSave(data);
      onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={isEditing ? 'Edit Widget' : 'Add Widget'}
      open={open}
      onClose={onClose}
      width={500}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} loading={saving}>
            {isEditing ? 'Save Changes' : 'Add Widget'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        {/* Widget Type Selection */}
        <Form.Item
          name="widget_type"
          label="Widget Type"
          rules={[{ required: true, message: 'Please select a widget type' }]}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {WIDGET_TYPES.map((type) => (
              <Card
                key={type.value}
                size="small"
                hoverable
                style={{
                  borderRadius: 2,
                  borderColor: widgetType === type.value ? 'var(--accent-ink)' : 'var(--border-default)',
                  backgroundColor: widgetType === type.value ? 'color-mix(in srgb, var(--accent-ink) 6%, var(--surface-1))' : 'var(--surface-1)',
                  boxShadow: 'none',
                }}
                onClick={() => form.setFieldValue('widget_type', type.value)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 20, color: 'var(--accent-ink)', flexShrink: 0 }}>{type.icon}</div>
                  <div>
                    <Text
                      strong
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: widgetType === type.value ? 'var(--accent-ink)' : 'var(--text-primary)',
                      }}
                    >
                      {type.label}
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {type.description}
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Form.Item>

        <Form.Item
          name="title"
          label="Widget Title"
          rules={[{ required: true, message: 'Please enter a title' }]}
        >
          <Input placeholder="e.g., My Upcoming Work" />
        </Form.Item>

        <Divider>Filters</Divider>

        {/* Assignee Filter */}
        <Form.Item name={['filter_config', 'assignee']} label="Assigned To">
          <Select allowClear placeholder="Anyone">
            <Select.Option value="me">Assigned to Me</Select.Option>
            <Select.Option value="unassigned">Unassigned</Select.Option>
            <Select.Option value="anyone">Anyone</Select.Option>
            <Select.OptGroup label="Specific User">
              {users?.map((tu) => (
                <Select.Option key={tu.userId} value={tu.userId}>
                  {tu.user.fullName || tu.user.email}
                </Select.Option>
              ))}
            </Select.OptGroup>
          </Select>
        </Form.Item>

        {/* Status Filter */}
        <Form.Item name={['filter_config', 'status']} label="Status">
          <Select mode="multiple" placeholder="Any status" options={STATUS_OPTIONS} />
        </Form.Item>

        {/* Category Filter */}
        <Form.Item name={['filter_config', 'categories']} label="Categories">
          <Select
            mode="multiple"
            placeholder="Any category"
            options={categories?.map((cat) => ({
              value: cat.id,
              label: cat.name,
            }))}
          />
        </Form.Item>

        {/* Tags Filter */}
        <Form.Item name={['filter_config', 'tags']} label="Tags">
          <Select
            mode="multiple"
            placeholder="Any tags"
            options={tags?.map((tag) => ({
              value: tag.id,
              label: tag.name,
            }))}
          />
        </Form.Item>

        <Form.Item name={['filter_config', 'tagsMatch']} label="Tags Match">
          <Select defaultValue="any">
            <Select.Option value="any">Any of selected tags</Select.Option>
            <Select.Option value="all">All selected tags</Select.Option>
          </Select>
        </Form.Item>

        {/* Due Date Filter */}
        <Form.Item name={['filter_config', 'dueDateRange']} label="Due Date Range">
          <Select allowClear placeholder="Any time" options={DATE_RANGE_OPTIONS} />
        </Form.Item>

        {dueDateRange === 'custom' && (
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={['filter_config', 'dueDateFrom']} label="From">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={['filter_config', 'dueDateTo']} label="To">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Template Filter */}
        <Form.Item name={['filter_config', 'templateId']} label="Specific Work Package Template">
          <Select
            allowClear
            showSearch
            placeholder="Any template"
            optionFilterProp="label"
            options={templates?.map((t) => ({
              value: t.id,
              label: t.title,
            }))}
          />
        </Form.Item>

        {/* Display Options (only for list type) */}
        {widgetType === 'list' && (
          <>
            <Divider>Display Options</Divider>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name={['display_config', 'maxItems']} label="Max Items">
                  <InputNumber min={1} max={50} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={['display_config', 'sortBy']} label="Sort By">
                  <Select options={SORT_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name={['display_config', 'sortOrder']} label="Sort Order">
              <Select>
                <Select.Option value="asc">Ascending (earliest first)</Select.Option>
                <Select.Option value="desc">Descending (latest first)</Select.Option>
              </Select>
            </Form.Item>

            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item name={['display_config', 'showAssignee']} valuePropName="checked" noStyle>
                <Switch /> <Text>Show Assignee</Text>
              </Form.Item>
              <Form.Item name={['display_config', 'showCategory']} valuePropName="checked" noStyle>
                <Switch /> <Text>Show Category</Text>
              </Form.Item>
              <Form.Item name={['display_config', 'showDueDate']} valuePropName="checked" noStyle>
                <Switch /> <Text>Show Due Date</Text>
              </Form.Item>
            </Space>
          </>
        )}

        <Divider>Size</Divider>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="grid_width" label="Width">
              <Select>
                <Select.Option value={1}>Small (1 column)</Select.Option>
                <Select.Option value={2}>Medium (2 columns)</Select.Option>
                <Select.Option value={3}>Large (3 columns)</Select.Option>
                <Select.Option value={4}>Full Width (4 columns)</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="grid_height" label="Height">
              <Select>
                <Select.Option value={1}>Short</Select.Option>
                <Select.Option value={2}>Medium</Select.Option>
                <Select.Option value={3}>Tall</Select.Option>
                <Select.Option value={4}>Extra Tall</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

export default WidgetConfigDrawer;
